import type {
  ConsumedTool,
  ConsumedToolDeployment,
  ConsumingPlatform,
} from '@efdigital/ll-cdk-lti-registration-api-types';

const PLACEHOLDER_PATTERN = /^TODO$/i;

export type PhaseStatus = 'incomplete' | 'awaiting_customer' | 'complete';
export type DeploymentPhaseStatus = 'none' | 'partial' | 'complete';
export type PlatformOverallStatus =
  | 'not_started'
  | 'awaiting_customer'
  | 'ready';

export function isPlaceholder(value: string | null | undefined): boolean {
  if (!value?.trim()) {
    return true;
  }

  return PLACEHOLDER_PATTERN.test(value.trim());
}

export function platformPhaseStatus(platform: ConsumingPlatform): PhaseStatus {
  if (isPlaceholder(platform.platformData.iss)) {
    return platform.name.trim() ? 'awaiting_customer' : 'incomplete';
  }

  return 'complete';
}

export function toolPhaseStatus(tool: ConsumedTool | null): PhaseStatus {
  if (!tool) {
    return 'incomplete';
  }

  const { client_id, jwks_url, authorizationEndpoint } = tool.platformData;
  const fields = [client_id, jwks_url, authorizationEndpoint];

  if (fields.every((field) => !isPlaceholder(field))) {
    return 'complete';
  }

  if (fields.some((field) => !isPlaceholder(field))) {
    return 'awaiting_customer';
  }

  return 'awaiting_customer';
}

export function deploymentPhaseStatus(
  deployments: ConsumedToolDeployment[]
): DeploymentPhaseStatus {
  if (deployments.length === 0) {
    return 'none';
  }

  const hasComplete = deployments.some(
    (deployment) => !isPlaceholder(deployment.platformDeploymentId)
  );

  if (hasComplete) {
    return 'complete';
  }

  return 'partial';
}

export function platformOverallStatus(
  platform: ConsumingPlatform,
  tools: ConsumedTool[],
  deploymentsByToolId: Record<string, ConsumedToolDeployment[]>
): PlatformOverallStatus {
  const phase1 = platformPhaseStatus(platform);
  const primaryTool = tools[0] ?? null;
  const phase2 = toolPhaseStatus(primaryTool);
  const deployments = primaryTool
    ? (deploymentsByToolId[primaryTool.id] ?? [])
    : [];
  const phase3 = deploymentPhaseStatus(deployments);

  if (phase1 === 'complete' && phase2 === 'complete' && phase3 === 'complete') {
    return 'ready';
  }

  if (phase1 === 'incomplete' && !primaryTool && phase3 === 'none') {
    return 'not_started';
  }

  return 'awaiting_customer';
}

export function overallStatusLabel(status: PlatformOverallStatus): string {
  switch (status) {
    case 'ready':
      return 'Ready';
    case 'not_started':
      return 'Not started';
    case 'awaiting_customer':
      return 'Awaiting customer';
  }
}

export function overallStatusBadgeClass(status: PlatformOverallStatus): string {
  switch (status) {
    case 'ready':
      return 'text-bg-success';
    case 'not_started':
      return 'text-bg-secondary';
    case 'awaiting_customer':
      return 'text-bg-warning';
  }
}

export function phaseStatusIcon(status: PhaseStatus): string {
  switch (status) {
    case 'complete':
      return 'ph:check-circle';
    case 'awaiting_customer':
      return 'ph:clock';
    case 'incomplete':
      return 'ph:circle';
  }
}

export function phaseStatusIconClass(status: PhaseStatus): string {
  switch (status) {
    case 'complete':
      return 'text-success';
    case 'awaiting_customer':
      return 'text-danger';
    case 'incomplete':
      return 'text-muted';
  }
}
